import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBlitzPlayback } from '../../hooks/useBlitzPlayback';
import { getAccessToken } from '../../utils/auth';
import BlitzTextDisplay from '../BlitzTextDisplay';
import PlayerControls from '../PlayerControls';
import BlitzBreadcrumb from '../BlitzBreadcrumb';
import BlitzMap from '../BlitzMap';
import ProcessingProgressBar from '../ProcessingProgressBar';

/**
 * BlitzPlayer — the rc2 per-key-point Blitz player. Mounted by `Blitz.js`
 * once Phase 3 has been kicked off — either complete OR in-flight (revised
 * 2026-05-18). When mounted mid-Phase-3 the queue grows silently as the
 * snapshot reports more chapters with `has_key_points`, so the user can
 * read / play the summaries that are already available without waiting for
 * the full run to finish. See `BlitzNotReadyView` for the truly-not-started
 * path (FR-010).
 *
 * Layout:
 *   Narrow / mobile  — single-column; "Map" button in the header opens a
 *     full-screen overlay.
 *   Wide (lg+)       — two-column; the map is a persistent sidebar on the
 *     right and the "Map" button is hidden.
 *
 * Props:
 *   - fileId: string — the file UUID, forwarded to the playback hook and the
 *     "back to chapters" / "Listen to full chapter" navigation handlers.
 *   - fileTitle: string — shown in the page chrome.
 *   - chapters: array of chapter records from `GET /files/{id}`.
 *   - progressSnapshot (optional): live `ProgressSnapshotResponse` from the
 *     parent's `useProgressPolling`. Forwarded to `useBlitzPlayback` so the
 *     queue refreshes when more chapters complete Phase 3.
 *   - isPhase3InFlight (optional, default false): true while Phase 3 is
 *     still running. Renders a small banner above the player that explains
 *     why some items may still be greyed out (no audio yet).
 */
function BlitzPlayer({
  fileId,
  fileTitle,
  chapters,
  progressSnapshot = null,
  isPhase3InFlight = false,
}) {
  const navigate = useNavigate();
  const audioRef = useRef(null);

  const [audioLoading, setAudioLoading] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);

  const {
    queue,
    currentIndex,
    currentItem,
    isPlaying,
    isLoading,
    error,
    isComplete,
    play,
    pause,
    next,
    prev,
    jumpToIndex,
    navigateUp,
    navigateDown,
    canNavigateUp,
    canNavigateDown,
    setIsPlaying,
    retry,
  } = useBlitzPlayback(fileId, chapters, progressSnapshot);

  const handleAudioEnded = useCallback(() => { next(); }, [next]);
  const handleAudioError = useCallback(() => { setAudioLoading(false); }, []);
  const handleAudioLoadStart = useCallback(() => { setAudioLoading(true); }, []);
  const handleAudioCanPlay = useCallback(() => { setAudioLoading(false); }, []);

  useEffect(() => {
    if (!currentItem || isComplete) return;
    // Mid-Phase-3 the current item may not have its audio rendered yet.
    // Skip the fetch entirely — the user can still read the text and use
    // the map to jump to playable items.
    if (!currentItem.audioUrl) {
      setAudioLoading(false);
      setAudioUrl(null);
      return;
    }
    let cancelled = false;
    const fetchAudio = async () => {
      try {
        setAudioLoading(true);
        const token = getAccessToken();
        const response = await fetch(currentItem.audioUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error(`Failed to fetch audio: ${response.status}`);
        const blob = await response.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setAudioLoading(false);
      } catch (e) {
        if (!cancelled) { setAudioLoading(false); next(); }
      }
    };
    fetchAudio();
    return () => {
      cancelled = true;
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, isComplete]);

  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;
    audioRef.current.src = audioUrl;
    if (isPlaying) {
      const p = audioRef.current.play();
      if (p !== undefined) p.catch(() => { setIsPlaying(false); });
    }
  }, [audioUrl, isPlaying, setIsPlaying]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying && !isComplete) {
      const p = audioRef.current.play();
      if (p !== undefined) p.catch(() => { setIsPlaying(false); });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, isComplete, setIsPlaying]);

  const handleExit = () => {
    if (audioRef.current) audioRef.current.pause();
    navigate(`/projects/${fileId}/chapters`);
  };

  return (
    // h-screen locks the layout to the viewport on all sizes so the text
    // area scrolls and the controls stay pinned at the bottom.
    <div className="h-screen bg-gray-900 flex flex-col">
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3 shrink-0">
        <div className="max-w-4xl mx-auto lg:max-w-none flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={handleExit} className="p-2 rounded-lg hover:bg-gray-700 transition-colors" title="Exit Blitz">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-white font-semibold truncate max-w-xs sm:max-w-md">{fileTitle}</h1>
              <p className="text-xs text-purple-400">⚡ Blitz Mode</p>
            </div>
          </div>
          {/* Map button — hidden on lg+ where the sidebar is always visible */}
          {queue.length > 0 && (
            <button
              onClick={() => setShowMap(true)}
              className="lg:hidden flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm text-white"
              title="Content Map"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              Map
            </button>
          )}
        </div>
      </header>

      {/* Body: single column on mobile, content + sidebar on lg+ */}
      <div className="flex-1 flex flex-col lg:flex-row min-h-0">
        <main className="flex-1 flex flex-col min-h-0 max-w-4xl mx-auto w-full p-4">
          {/* Mid-Phase-3 banner — explains why some items in the map / queue
              may still be greyed out and reassures the user that more
              content will appear automatically (revised 2026-05-18). */}
          {isPhase3InFlight && !isLoading && !error && (
            <div
              role="status"
              data-testid="blitz-player-in-flight-notice"
              className="mb-4 rounded-lg p-3 border bg-blue-900/30 border-blue-700 text-blue-100"
            >
              <p className="text-sm leading-snug mb-1">
                <span className="font-semibold">Blitz is still being generated.</span>{' '}
                Summaries and key points appear here as they become available — open
                the Map to jump to anything that's already ready.
              </p>
              {progressSnapshot && (
                <ProcessingProgressBar snapshot={progressSnapshot} />
              )}
            </div>
          )}
          {isLoading && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4" />
                <p className="text-gray-400">Loading summaries...</p>
              </div>
            </div>
          )}
          {error && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-red-400 mb-4">{error}</p>
                <button onClick={retry} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                  Retry
                </button>
              </div>
            </div>
          )}
          {isComplete && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-4xl mb-4">🎉</div>
                <h2 className="text-xl text-white font-semibold mb-2">Blitz Complete!</h2>
                <p className="text-gray-400 mb-6">You've explored all summaries</p>
                <button onClick={handleExit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
                  Back to Chapters
                </button>
              </div>
            </div>
          )}
          {!isLoading && !error && !isComplete && currentItem && (
            <div className="flex-1 flex flex-col min-h-0">
              <div className="mb-4 shrink-0">
                <BlitzBreadcrumb breadcrumb={currentItem.breadcrumb} level={currentItem.level} />
              </div>
              <div className="flex-1 overflow-y-auto min-h-0 mb-6">
                <BlitzTextDisplay
                  title={currentItem.title}
                  text={currentItem.text}
                  isLoading={audioLoading}
                />
                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate(`/projects/${fileId}/chapters?autoplay=${currentItem.chapterId}`)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-400 hover:text-purple-300 hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15.536a5 5 0 001.414 1.414m2.828-9.9a9 9 0 0112.728 0" />
                    </svg>
                    Listen to full chapter
                  </button>
                </div>
              </div>
              <div className="py-4 shrink-0">
                <PlayerControls
                  isPlaying={isPlaying}
                  isLoading={audioLoading}
                  disabled={isComplete}
                  upTitle="Previous Chapter"
                  downTitle="Next Chapter"
                  onPlay={play}
                  onPause={pause}
                  onPrev={prev}
                  onNext={next}
                  onUp={navigateUp}
                  onDown={navigateDown}
                  hasPrev={currentIndex > 0}
                  hasNext={currentIndex < queue.length - 1}
                  canUp={canNavigateUp}
                  canDown={canNavigateDown}
                />
                <div className="mt-4 text-center">
                  <p className="text-xs text-gray-500">{currentIndex + 1} of {queue.length}</p>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Persistent sidebar — lg+ only */}
        {queue.length > 0 && (
          <aside className="hidden lg:flex lg:flex-col w-80 shrink-0 border-l border-gray-700">
            <BlitzMap
              queue={queue}
              currentIndex={currentIndex}
              onSelect={(index) => { jumpToIndex(index); }}
              onClose={() => {}}
              inline
            />
          </aside>
        )}
      </div>

      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
        onError={handleAudioError}
        onLoadStart={handleAudioLoadStart}
        onCanPlay={handleAudioCanPlay}
        preload="auto"
      />

      {/* Overlay map — mobile / narrow only */}
      {showMap && (
        <BlitzMap
          queue={queue}
          currentIndex={currentIndex}
          onSelect={(index) => { jumpToIndex(index); }}
          onClose={() => setShowMap(false)}
        />
      )}
    </div>
  );
}

export default BlitzPlayer;
